import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { queryClient } from '@/config/query-client'
import { refreshToken } from '@/services/api/auth.service'
import { isServerUnavailable } from '@/services/api/client'
import { getSubscriptionStatus } from '@/services/api/subscription.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'

/** Backoff between refresh attempts while the server is unreachable (e.g. restarting). */
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000]

async function refreshUntilReachable(isCancelled: () => boolean) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await refreshToken()
    } catch (error) {
      if (!isServerUnavailable(error) || attempt >= RETRY_DELAYS_MS.length || isCancelled()) throw error
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]))
    }
  }
}

export function AuthLayout() {
  const navigate = useNavigate()
  const { accessToken, setLoading, isLoading } = useAuthStore()
  const [isInitializing, setIsInitializing] = useState(true)
  const [serverUnavailable, setServerUnavailable] = useState(false)
  const [initAttempt, setInitAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false

    const initAuth = async () => {
      // User just completed /auth/login and is mid-flow selecting a workspace.
      // No access token exists yet (that only arrives after /auth/login/workspace),
      // so skip the refresh attempt entirely.
      const { authStep } = useAuthStore.getState()
      if (authStep === 'workspace') {
        setIsInitializing(false)
        setLoading(false)
        return
      }

      if (useAuthStore.getState().accessToken) {
        // Token already exists (workspace just selected). Permissions load via usePermission().
        setIsInitializing(false)
        setLoading(false)
        return
      }

      try {
        // Restores the token, user, workspace and superUser/developer flags.
        await refreshUntilReachable(() => cancelled)
        if (cancelled) return

        // Members without subscription:view get a 403 here; that must not end the session.
        try {
          const subscription = await getSubscriptionStatus()
          useSubscriptionStore.getState().setStatus(subscription.status)
          useSubscriptionStore.getState().setCancelAtPeriodEnd(subscription.cancelAtPeriodEnd)
        } catch {
          // subscription status is optional for the session
        }
      } catch (error) {
        if (cancelled) return
        if (isServerUnavailable(error)) {
          // The session may well be valid; keep it and let the user retry.
          setServerUnavailable(true)
          return
        }
        // E-mailed owner links survive the sign-in detour (the token stays in the fragment).
        if (window.location.pathname === '/confirm') {
          sessionStorage.setItem('returnUrl', window.location.pathname + window.location.hash)
        }
        useAuthStore.getState().logout()
        queryClient.clear()
        usePermissionStore.getState().clear()
        useSubscriptionStore.getState().clear()
        navigate('/login', { replace: true })
      } finally {
        if (!cancelled) {
          setIsInitializing(false)
          setLoading(false)
        }
      }
    }

    initAuth()
    return () => {
      cancelled = true
    }
    // navigate (data router) and setLoading (store action) are stable, so this runs once per attempt.
  }, [initAttempt, navigate, setLoading])

  if (serverUnavailable) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-sm space-y-3 text-center">
          <h1 className="text-lg font-semibold">Can't reach the server</h1>
          <p className="text-sm text-neutral-500">
            You're still signed in. Check your connection, or try again in a moment.
          </p>
          <Button
            onClick={() => {
              setServerUnavailable(false)
              setIsInitializing(true)
              setInitAttempt((n) => n + 1)
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (isInitializing || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-8 w-1/2" />
        </div>
      </div>
    )
  }

  // Allow rendering during the workspace-selection step — the user is authenticated
  // (they passed /auth/login) but doesn't have an access token until they pick a workspace.
  const { authStep } = useAuthStore.getState()
  if (!accessToken && authStep !== 'workspace') {
    return null
  }

  return <Outlet />
}